const DAY_IN_MS = 24 * 60 * 60 * 1000;
const PACKAGING_PRICING = { rubber: 0, paper: 5 };

// Statuses that mean "the customer has paid". These all contribute to revenue,
// AOV, profit, top-items, sales-by-staff, etc. Keep in sync with the rider
// workflow used by restaurant-ordering (lib/order-status.js) — `preparing`,
// `dispatched`, and `on_the_way` are all post-payment delivery states that
// were historically missing from this list, leaving real revenue uncounted
// (10+ `dispatched` orders were silently dropped before this commit).
export const ANALYTICS_REVENUE_STATUSES = [
  'paid',
  'preparing',
  'ready',
  'dispatched',
  'on_the_way',
  'picked_up',
  'delivered',
];

// Order of statuses for the status-distribution panel. Same set plus the
// non-revenue terminal/initial states (`pending_payment`, `cancelled`,
// `refunded`) so every row in the orders table maps to a visible bucket
// instead of vanishing from the panel.
export const ANALYTICS_STATUS_ORDER = [
  'pending_payment',
  'paid',
  'preparing',
  'ready',
  'dispatched',
  'on_the_way',
  'picked_up',
  'delivered',
  'cancelled',
  'refunded',
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const normalizeIdentityPart = (value) => String(value || '').trim().toLowerCase();
const getOrderAdjustmentByType = (order, type) => {
  if (!order || !Array.isArray(order.adjustments)) return null;

  for (let index = order.adjustments.length - 1; index >= 0; index -= 1) {
    const adjustment = order.adjustments[index];
    if (adjustment?.type === type) return adjustment;
  }

  return null;
};
const parseDeliveryFeeFromNotes = (notes) => {
  if (!notes) return 0;

  const match = String(notes).match(/(?:^|\|\s*)Delivery Fee:\s*(?:GHS|GHC|GH\$|GHc|C)?\s*(\d+(?:\.\d+)?)/i);
  return toNumber(match?.[1]);
};

const addPaymentMethodRevenue = (acc, order, revenue) => {
  if (order.paymentMethod === 'split' && Array.isArray(order.splitPayments) && order.splitPayments.length > 0) {
    const splitTotal = order.splitPayments.reduce((sum, leg) => sum + toNumber(leg.amount), 0);

    // If the split legs sum to a positive amount, scale each leg by the
    // reconstructed-vs-recorded revenue ratio so the per-method totals add up
    // to the order's true revenue (handles amends that bump the total above
    // the recorded leg amounts).
    if (splitTotal > 0) {
      const ratio = revenue / splitTotal;
      order.splitPayments.forEach((leg) => {
        const method = leg.method || 'Unknown';
        acc[method] = (acc[method] || 0) + (toNumber(leg.amount) * ratio);
      });
      return;
    }

    // Split legs sum to 0 (zeroed-out by a bad amend or import) but the order
    // itself still has revenue. Spread it evenly across the recorded methods
    // so the money doesn't vanish from the by-method chart.
    if (revenue > 0) {
      const perLeg = revenue / order.splitPayments.length;
      order.splitPayments.forEach((leg) => {
        const method = leg.method || 'Unknown';
        acc[method] = (acc[method] || 0) + perLeg;
      });
    }
    return;
  }

  const method = order.paymentMethod || 'Unknown';
  acc[method] = (acc[method] || 0) + revenue;
};

const getItemEntries = (order) => Array.isArray(order.items) ? order.items : [];
const buildExpenseSignature = (expense) => {
  const timestamp = normalizeIdentityPart(expense?.timestamp);
  const by = normalizeIdentityPart(expense?.by);
  const category = normalizeIdentityPart(expense?.category);
  const description = normalizeIdentityPart(expense?.description);
  const amount = toNumber(expense?.amount).toFixed(2);

  if (!timestamp || !by || !category) return '';

  return [timestamp, by, category, amount, description].join('|');
};

export const isAnalyticsRevenueOrder = (order) => ANALYTICS_REVENUE_STATUSES.includes(order?.status);

export const getAnalyticsTimestamp = (order) => order?.paidAt || order?.timestamp;
export const getOrderItemRevenue = (order) => getItemEntries(order)
  .reduce((sum, item) => sum + (toNumber(item.quantity) * toNumber(item.price)), 0);
export const getOrderPackagingRevenue = (order) => {
  if (Array.isArray(order?.bundles) && order.bundles.length > 0) {
    const explicitBundlePackaging = order.bundles.some((bundle) => bundle?.packaging);

    if (explicitBundlePackaging) {
      return order.bundles.reduce((sum, bundle) => {
        const items = bundle?.items;
        const itemList = Array.isArray(items) ? items : Object.values(items || {});
        const hasItems = itemList.some((item) => toNumber(item?.quantity) > 0);
        if (!hasItems) return sum;

        return sum + toNumber(PACKAGING_PRICING[bundle.packaging || 'rubber']);
      }, 0);
    }
  }

  return order?.packaging === 'paper' ? PACKAGING_PRICING.paper : 0;
};
export const getOrderDeliveryRevenue = (order) => {
  const adjustment = getOrderAdjustmentByType(order, 'delivery_fee');
  if (adjustment) {
    return toNumber(adjustment.amount ?? adjustment.fee);
  }

  return parseDeliveryFeeFromNotes(order?.notes);
};
// Discount applied to an order, in cedis. `percent` is the source of truth so
// the discount tracks the items subtotal across amends (adding an item extends
// the same percentage to it; removing one shrinks the discount instead of
// leaving a stale amount that could exceed what's left). The stored `amount` is
// an audit snapshot of what it was worth on the day and is only read as a
// fallback for records that somehow carry no percent.
//
// Clamped to [0, itemsSubtotal]: packaging and delivery are never discounted,
// and a discount can never turn an order negative.
export const getOrderDiscountAmount = (order) => {
  const adjustment = getOrderAdjustmentByType(order, 'discount');
  if (!adjustment) return 0;

  const itemsSubtotal = getOrderItemRevenue(order);
  const percent = toNumber(adjustment.percent);
  const raw = percent > 0 ? (itemsSubtotal * percent) / 100 : toNumber(adjustment.amount);

  return Math.min(Math.max(raw, 0), itemsSubtotal);
};
export const getReconstructedOrderTotal = (order) => getOrderItemRevenue(order)
  + getOrderPackagingRevenue(order)
  + getOrderDeliveryRevenue(order)
  - getOrderDiscountAmount(order);
export const getOrderRevenueAmount = (order) => {
  const recordedTotal = toNumber(order?.total) || toNumber(order?.paymentAmount);
  const reconstructedTotal = getReconstructedOrderTotal(order);

  if (reconstructedTotal > recordedTotal) return reconstructedTotal;
  return recordedTotal;
};
export const calculateAmendedOrderTotal = (order, bundles = []) => {
  const nextItems = bundles.flatMap((bundle) => {
    const items = bundle?.items;
    if (!items) return [];
    return Array.isArray(items) ? items : Object.values(items);
  });
  // Use the reconstructed total directly: getOrderRevenueAmount keeps the
  // larger of recorded vs reconstructed (an analytics safety net), which would
  // stop an amend from ever lowering the total — e.g. removing packaging.
  const draftOrder = {
    ...order,
    bundles,
    items: nextItems,
  };

  return getReconstructedOrderTotal(draftOrder);
};
export const getExpenseIdentityKeys = (expense) => Array.from(new Set(
  [
    normalizeIdentityPart(expense?.id),
    normalizeIdentityPart(expense?.supabase_id),
    buildExpenseSignature(expense),
  ].filter(Boolean)
));

// Newer lastModified/timestamp wins wholesale; ids/supabase_ids are preserved
// from whichever side carries them.
const combineExpenses = (a, b) => {
  const aTime = new Date(a?.lastModified || a?.timestamp || 0).getTime();
  const bTime = new Date(b?.lastModified || b?.timestamp || 0).getTime();
  const [older, newer] = bTime >= aTime ? [a, b] : [b, a];
  return {
    ...older,
    ...newer,
    id: older?.id || newer?.id,
    supabase_id: newer?.supabase_id || older?.supabase_id,
  };
};

export const dedupeExpenses = (expenses = []) => {
  const merged = []; // removed slots are tombstoned with null, filtered at the end
  const indexByKey = new Map();

  const upsertExpense = (incoming) => {
    const keys = getExpenseIdentityKeys(incoming);
    // An incoming record can match identity keys belonging to SEVERAL existing
    // entries (e.g. one known by id, another by supabase_id). Collapse all of
    // them, not just the first, or a phantom duplicate survives.
    const matchedIndices = Array.from(new Set(
      keys.map((key) => indexByKey.get(key)).filter((index) => index !== undefined)
    ));

    if (matchedIndices.length === 0) {
      const nextIndex = merged.push(incoming) - 1;
      keys.forEach((key) => indexByKey.set(key, nextIndex));
      return;
    }

    const survivorIndex = Math.min(...matchedIndices);
    let combined = incoming;
    matchedIndices.forEach((index) => {
      combined = combineExpenses(merged[index], combined);
      if (index !== survivorIndex) merged[index] = null;
    });
    merged[survivorIndex] = combined;

    // Re-point every key that aimed at any merged-away entry (or the incoming
    // record) to the survivor.
    const merging = new Set(matchedIndices);
    indexByKey.forEach((value, key) => {
      if (merging.has(value)) indexByKey.set(key, survivorIndex);
    });
    getExpenseIdentityKeys(combined).forEach((key) => indexByKey.set(key, survivorIndex));
  };

  expenses.forEach(upsertExpense);
  return merged.filter((entry) => entry !== null);
};

export const isSameLocalDay = (timestamp, comparisonDate = new Date()) => {
  const value = new Date(timestamp);
  return value.getFullYear() === comparisonDate.getFullYear()
    && value.getMonth() === comparisonDate.getMonth()
    && value.getDate() === comparisonDate.getDate();
};

export const createDateRangeMatcher = ({
  dateFilter,
  customDate,
  customDateRange,
  now = new Date(),
}) => {
  if (dateFilter === 'all') {
    return () => true;
  }

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const normalizeStart = (value) => new Date(`${value}T00:00:00`);
  const normalizeEnd = (value) => new Date(`${value}T23:59:59.999`);

  if (dateFilter === 'custom') {
    if (customDate) {
      const start = normalizeStart(customDate);
      const end = normalizeEnd(customDate);
      return (timestamp) => {
        const value = new Date(timestamp);
        return value >= start && value <= end;
      };
    }

    if (!customDateRange?.start || !customDateRange?.end) {
      return () => false;
    }

    // Tolerate a range entered in reverse (end before start) by ordering the
    // bounds — string compare is safe for YYYY-MM-DD.
    const [lowDate, highDate] = customDateRange.start <= customDateRange.end
      ? [customDateRange.start, customDateRange.end]
      : [customDateRange.end, customDateRange.start];
    const start = normalizeStart(lowDate);
    const end = normalizeEnd(highDate);
    return (timestamp) => {
      const value = new Date(timestamp);
      return value >= start && value <= end;
    };
  }

  let start = null;
  let end = null;
  // Default upper bound for all "current period" filters: end of today. Stops
  // future-dated rows (badly synced server clocks, manually backdated entries,
  // mistimed paidAt fields) from leaking into Today / This Week / This Month
  // / This Quarter / This Year just because no upper bound was set.
  const endOfToday = new Date(today.getTime() + DAY_IN_MS - 1);

  switch (dateFilter) {
    case 'today':
      start = today;
      end = endOfToday;
      break;
    case 'yesterday':
      start = new Date(today.getTime() - DAY_IN_MS);
      end = new Date(today.getTime() - 1);
      break;
    case 'last7days':
      start = new Date(today.getTime() - (6 * DAY_IN_MS));
      end = endOfToday;
      break;
    case 'week': {
      start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      end = endOfToday;
      break;
    }
    case 'last30days':
      start = new Date(today.getTime() - (29 * DAY_IN_MS));
      end = endOfToday;
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = endOfToday;
      break;
    case 'quarter': {
      const quarter = Math.floor(now.getMonth() / 3);
      start = new Date(now.getFullYear(), quarter * 3, 1);
      end = endOfToday;
      break;
    }
    case 'year':
      start = new Date(now.getFullYear(), 0, 1);
      end = endOfToday;
      break;
    default:
      return () => true;
  }

  return (timestamp) => {
    const value = new Date(timestamp);
    if (value < start) return false;
    if (end && value > end) return false;
    return true;
  };
};

export const buildAnalyticsSnapshot = ({
  orders = [],
  expenses = [],
  matchesRange,
  today = new Date(),
}) => {
  const uniqueExpenses = dedupeExpenses(expenses);
  const reportOrders = orders.filter((order) => matchesRange(getAnalyticsTimestamp(order)));
  const revenueOrders = reportOrders.filter(isAnalyticsRevenueOrder);
  // getOrderRevenueAmount reconstructs the order total (iterating items), and it
  // is read 5-6 times per order below. Compute it once per order and reuse.
  const revenueByOrder = new Map();
  const revenueOf = (order) => {
    if (!revenueByOrder.has(order)) revenueByOrder.set(order, getOrderRevenueAmount(order));
    return revenueByOrder.get(order);
  };
  // Today's KPI tracks settled (paid+) activity so it lines up with Revenue/AOV/Profit.
  const todaysOrders = revenueOrders.filter((order) => isSameLocalDay(getAnalyticsTimestamp(order), today));
  const totalRevenue = revenueOrders.reduce((sum, order) => sum + revenueOf(order), 0);
  // refundDue is only ever set on already-paid orders (see amend flow in
  // App.js), so reportOrders and revenueOrders yield the same total here —
  // use revenueOrders for clarity / defensive against stale fields.
  const totalRefunds = revenueOrders.reduce((sum, order) => sum + toNumber(order.refundDue), 0);
  // dueAmount, on the other hand, is set on orders that an amend bumped BACK
  // to pending_payment because the new total exceeded what was already paid —
  // those are legitimate outstanding collections that the user still needs to
  // chase, so we walk the full reportOrders set, not just revenueOrders.
  const totalDue = reportOrders.reduce((sum, order) => sum + toNumber(order.dueAmount), 0);
  const totalExpenses = uniqueExpenses
    .filter((expense) => expense.status === 'approved' && matchesRange(expense.timestamp))
    .reduce((sum, expense) => sum + toNumber(expense.amount), 0);
  const avgOrderValue = revenueOrders.length ? totalRevenue / revenueOrders.length : 0;

  const salesByUserMap = {};
  revenueOrders.forEach((order) => {
    const key = order.processedBy || 'Unknown';
    salesByUserMap[key] = (salesByUserMap[key] || 0) + revenueOf(order);
  });

  const revenueByMethodMap = {};
  revenueOrders.forEach((order) => addPaymentMethodRevenue(revenueByMethodMap, order, revenueOf(order)));

  const revenueBySourceMap = {};
  revenueOrders.forEach((order) => {
    const source = order.orderSource || 'Unknown';
    if (!revenueBySourceMap[source]) revenueBySourceMap[source] = { source, orders: 0, revenue: 0 };
    revenueBySourceMap[source].orders += 1;
    revenueBySourceMap[source].revenue += revenueOf(order);
  });

  const topItemsMap = {};
  revenueOrders.forEach((order) => {
    getItemEntries(order).forEach((item) => {
      if (!item?.name) return;
      const quantity = toNumber(item.quantity);
      if (!topItemsMap[item.name]) topItemsMap[item.name] = { count: 0, revenue: 0 };
      topItemsMap[item.name].count += quantity;
      topItemsMap[item.name].revenue += quantity * toNumber(item.price);
    });
  });

  const statusDistributionMap = ANALYTICS_STATUS_ORDER.reduce((acc, status) => {
    acc[status] = { count: 0, revenue: 0 };
    return acc;
  }, {});
  reportOrders.forEach((order) => {
    if (!statusDistributionMap[order.status]) return;
    statusDistributionMap[order.status].count += 1;
    if (isAnalyticsRevenueOrder(order)) {
      statusDistributionMap[order.status].revenue += revenueOf(order);
    }
  });

  const dailyTrendMap = new Map();
  revenueOrders.forEach((order) => {
    const date = new Date(getAnalyticsTimestamp(order));
    if (Number.isNaN(date.getTime())) return;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const previous = dailyTrendMap.get(key) || { dateKey: key, revenue: 0, orders: 0 };
    dailyTrendMap.set(key, {
      dateKey: key,
      revenue: previous.revenue + revenueOf(order),
      orders: previous.orders + 1,
    });
  });

  // Hourly trend describes peak service hours; use revenueOrders (paid+) so it
  // lines up with the headline Orders KPI and dailyTrend. Otherwise the chart
  // would double-count pending_payment carts that never became revenue.
  const hourlyTrend = Array.from({ length: 24 }, (_, hour) => ({ hour, revenue: 0, orders: 0 }));
  revenueOrders.forEach((order) => {
    const date = new Date(getAnalyticsTimestamp(order));
    if (Number.isNaN(date.getTime())) return;
    const hour = date.getHours();
    hourlyTrend[hour].orders += 1;
    hourlyTrend[hour].revenue += revenueOf(order);
  });

  const prepMinutes = revenueOrders
    .map((order) => (order.readyAt && order.paidAt)
      ? (new Date(order.readyAt).getTime() - new Date(order.paidAt).getTime()) / 60000
      : null)
    .filter((value) => typeof value === 'number' && value >= 0 && value <= 24 * 60);
  const fulfillmentMinutes = revenueOrders
    .map((order) => (order.completedAt && order.paidAt)
      ? (new Date(order.completedAt).getTime() - new Date(order.paidAt).getTime()) / 60000
      : null)
    .filter((value) => typeof value === 'number' && value >= 0 && value <= 24 * 60);

  return {
    reportOrders,
    revenueOrders,
    analyticsData: {
      // Match Revenue/AOV/Profit: count only settled (paid/ready/picked_up/delivered) orders.
      // reportOrders (which still includes pending_payment) is exposed separately for the
      // status-mix and outstanding-collections panels lower on the page.
      totalOrders: revenueOrders.length,
      totalRevenue,
      avgOrderValue,
      todaysOrders: todaysOrders.length,
      totalExpenses,
      profit: totalRevenue - totalRefunds - totalExpenses,
    },
    salesByUser: Object.entries(salesByUserMap).sort((a, b) => b[1] - a[1]),
    revenueByMethod: Object.entries(revenueByMethodMap).sort((a, b) => b[1] - a[1]),
    revenueBySource: Object.values(revenueBySourceMap).sort((a, b) => b.revenue - a.revenue),
    topItems: Object.entries(topItemsMap)
      .map(([name, data]) => ({ name, count: data.count, revenue: data.revenue }))
      .sort((a, b) => b.count - a.count),
    statusDistribution: ANALYTICS_STATUS_ORDER.map((status) => ({ status, ...statusDistributionMap[status] })),
    totalRefunds,
    totalDue,
    dailyTrend: Array.from(dailyTrendMap.values()).sort((a, b) => a.dateKey.localeCompare(b.dateKey)),
    hourlyTrend,
    prepTimeStats: {
      count: prepMinutes.length,
      avgMinutes: prepMinutes.length ? prepMinutes.reduce((sum, value) => sum + value, 0) / prepMinutes.length : 0,
    },
    fulfillmentTimeStats: {
      count: fulfillmentMinutes.length,
      avgMinutes: fulfillmentMinutes.length ? fulfillmentMinutes.reduce((sum, value) => sum + value, 0) / fulfillmentMinutes.length : 0,
    },
  };
};

export const buildEodReport = ({ dateStr, orders = [], expenses = [] }) => {
  const matchesRange = createDateRangeMatcher({
    dateFilter: 'custom',
    customDate: dateStr,
    customDateRange: { start: '', end: '' },
    now: new Date(`${dateStr}T12:00:00`),
  });
  const snapshot = buildAnalyticsSnapshot({
    orders,
    expenses,
    matchesRange,
    today: new Date(`${dateStr}T12:00:00`),
  });
  const completedOrders = snapshot.revenueOrders.length;
  const pendingOrders = snapshot.reportOrders.filter((order) => order.status === 'pending_payment').length;
  const onlineOrders = snapshot.reportOrders.filter((order) => order.orderType === 'online').length;
  const inPersonOrders = snapshot.reportOrders.filter((order) => order.orderType === 'in-person').length;
  const byStaff = snapshot.salesByUser.reduce((acc, [name, revenue]) => {
    const ordersForStaff = snapshot.revenueOrders.filter((order) => (order.processedBy || 'Unknown') === name);
    // Per-method totals let a supervisor check each till (cash, MoMo) against
    // what that staff member keyed in.
    const byMethod = {};
    ordersForStaff.forEach((order) => addPaymentMethodRevenue(byMethod, order, getOrderRevenueAmount(order)));
    acc[name] = { orders: ordersForStaff.length, revenue, byMethod };
    return acc;
  }, {});
  const topItems = snapshot.topItems
    .slice(0, 8)
    .map((item) => [item.name, { qty: item.count, revenue: item.revenue }]);
  const dayExpenses = dedupeExpenses(expenses)
    .filter((expense) => expense.status === 'approved' && matchesRange(expense.timestamp));

  return {
    dateStr,
    totalRevenue: snapshot.analyticsData.totalRevenue,
    totalOrders: snapshot.reportOrders.length,
    completedOrders,
    pendingOrders,
    byMethod: Object.fromEntries(snapshot.revenueByMethod),
    topItems,
    totalExpenses: snapshot.analyticsData.totalExpenses,
    netProfit: snapshot.analyticsData.profit,
    byStaff,
    avgOrderValue: snapshot.analyticsData.avgOrderValue,
    onlineOrders,
    inPersonOrders,
    dayExpenses,
  };
};
